import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Wallet, TrendingUp, TrendingDown, Landmark, AlertTriangle, Info,
  AlertCircle, LineChart, FileDown, School, HeartHandshake, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { FinanceAssistant } from '../components/finance/FinanceAssistant';
import { FarmAnalysisCard } from '../components/finance/FarmAnalysisCard';
import { BoardPackCard } from '../components/finance/BoardPackCard';
import { YearCloseReminder } from '../components/finance/YearCloseReminder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROGRAM_META = {
  school: { label: 'School Program', icon: School, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
  women: { label: 'Women Empowerment', icon: HeartHandshake, color: 'text-pink-600', bg: 'bg-pink-50 dark:bg-pink-900/20' },
  general: { label: 'General / Org', icon: Building2, color: 'text-slate-600', bg: 'bg-slate-50 dark:bg-slate-800/40' },
};

const SEVERITY = {
  critical: { icon: AlertCircle, cls: 'border-red-300 bg-red-50 dark:bg-red-900/15 text-red-700 dark:text-red-300' },
  warning: { icon: AlertTriangle, cls: 'border-amber-300 bg-amber-50 dark:bg-amber-900/15 text-amber-700 dark:text-amber-300' },
  info: { icon: Info, cls: 'border-blue-200 bg-blue-50 dark:bg-blue-900/15 text-blue-700 dark:text-blue-300' },
};

const REPORTS = [
  { key: 'income_statement', label: 'Income Statement' },
  { key: 'expense_report', label: 'Expense Report' },
  { key: 'income_by_program', label: 'Income by Program' },
  { key: 'expenses_by_program', label: 'Expenses by Program' },
  { key: 'monthly_summary', label: 'Monthly Summary' },
  { key: 'cashflow', label: 'Cash-flow Summary' },
  { key: 'loans_summary', label: 'Loan Summary' },
  { key: 'maize_summary', label: 'Maize Buy-back Summary' },
  { key: 'school_report', label: 'School Financial Report' },
  { key: 'women_report', label: 'Women Project Report' },
  { key: 'funding_source_report', label: 'Funding Source Report' },
  { key: 'requisition_report', label: 'Requisition Report' },
  { key: 'cash_withdrawal_report', label: 'Cash Withdrawal Report' },
  { key: 'vendor_report', label: 'Vendor / Professional Services' },
];

export default function FinanceOverviewPage() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [filters, setFilters] = useState({ program: 'all', date_from: '', date_to: '' });
  const [reportKey, setReportKey] = useState('income_statement');
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {
        ...(filters.program !== 'all' ? { program: filters.program } : {}),
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
      };
      const [o, i, f] = await Promise.all([
        axios.get(`${API_URL}/api/finance/overview`, { params }),
        axios.get(`${API_URL}/api/finance/insights`),
        axios.get(`${API_URL}/api/finance/forecast`),
      ]);
      setData(o.data);
      setInsights(i.data.insights || []);
      setForecast(f.data);
    } catch (e) {
      toast.error('Failed to load finance overview');
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const downloadReport = async (format) => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ format });
      if (filters.program !== 'all') params.append('program', filters.program);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      const res = await axios.get(`${API_URL}/api/finance/reports/${reportKey}?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportKey}.${format === 'excel' ? 'xlsx' : format === 'csv' ? 'csv' : 'pdf'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error('Report download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const KPIS = [
    { label: 'Total Income', value: data.total_income, icon: TrendingUp, cls: 'text-green-600' },
    { label: 'Total Expenses', value: data.total_expenses, icon: TrendingDown, cls: 'text-red-600' },
    { label: 'Net Balance', value: data.net_balance, icon: Wallet, cls: data.net_balance >= 0 ? 'text-green-600' : 'text-red-600' },
    { label: 'Cash Position', value: data.cash_position, icon: Wallet, cls: data.cash_position >= 0 ? 'text-emerald-600' : 'text-red-600' },
    { label: 'Receivables', value: data.outstanding_receivables, icon: TrendingUp, cls: 'text-amber-600' },
    { label: 'Committed Payables', value: data.outstanding_payables, icon: TrendingDown, cls: 'text-orange-600' },
    { label: 'Loans Outstanding', value: data.loans_outstanding, icon: Landmark, cls: 'text-purple-600' },
  ];

  return (
    <div className="space-y-6" data-testid="finance-overview-page">
      <YearCloseReminder />
      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Program</label>
            <Select value={filters.program} onValueChange={(v) => setFilters({ ...filters, program: v })}>
              <SelectTrigger className="w-52" data-testid="overview-program-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs (Consolidated)</SelectItem>
                <SelectItem value="school">School Program</SelectItem>
                <SelectItem value="women">Women Empowerment</SelectItem>
                <SelectItem value="general">General / Organisational</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" className="w-40" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="overview-date-from" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" className="w-40" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="overview-date-to" />
          </div>
          {(filters.program !== 'all' || filters.date_from || filters.date_to) && (
            <Button variant="ghost" size="sm" onClick={() => setFilters({ program: 'all', date_from: '', date_to: '' })}>
              Clear filters
            </Button>
          )}
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="finance-kpis">
        {KPIS.map(({ label, value, icon: Icon, cls }) => (
          <Card key={label} className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Icon className={`h-3.5 w-3.5 ${cls}`} /> {label}
              </div>
              <p className={`text-lg font-heading font-bold ${cls}`}>{formatCurrency(value)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Workflow KPIs */}
      {data.workflow && (
        <div className="grid grid-cols-3 gap-3" data-testid="workflow-kpis">
          <Card className="cursor-pointer card-hover" onClick={() => window.location.assign('/finance/approvals')}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Pending Requisitions</p>
              <p className="text-xl font-heading font-bold text-blue-600" data-testid="overview-pending-reqs">{data.workflow.pending_requisitions}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer card-hover" onClick={() => window.location.assign('/finance/approvals')}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Approved — Awaiting Payment</p>
              <p className="text-xl font-heading font-bold text-emerald-600">{data.workflow.approved_awaiting_payment}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer card-hover" onClick={() => window.location.assign('/finance/approvals')}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Unreconciled Withdrawals</p>
              <p className="text-xl font-heading font-bold text-amber-600">{data.workflow.unreconciled_withdrawals}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <LineChart className="h-4 w-4" /> Monthly Income vs Expenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.monthly_series.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-muted-foreground">No data for this period</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.monthly_series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="income" name="Income" fill="#22C55E" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Program split */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">By Program</CardTitle>
            <CardDescription>Financial separation: School vs Women vs General</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(PROGRAM_META).map(([key, meta]) => {
              const p = data.by_program[key] || { income: 0, expenses: 0 };
              return (
                <div key={key} className={`rounded-lg p-3 ${meta.bg}`} data-testid={`program-split-${key}`}>
                  <div className={`flex items-center gap-2 font-medium text-sm ${meta.color}`}>
                    <meta.icon className="h-4 w-4" /> {meta.label}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-1.5 text-xs">
                    <div><span className="text-muted-foreground">In</span><p className="font-bold text-green-600">{formatCurrency(p.income)}</p></div>
                    <div><span className="text-muted-foreground">Out</span><p className="font-bold text-red-600">{formatCurrency(p.expenses)}</p></div>
                    <div><span className="text-muted-foreground">Net</span><p className={`font-bold ${p.income - p.expenses >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(p.income - p.expenses)}</p></div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Insights */}
        <Card data-testid="finance-insights">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Financial Insights
            </CardTitle>
            <CardDescription>Automatic checks on your records</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 max-h-80 overflow-y-auto">
            {insights.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No issues detected — records look healthy.</p>
            ) : insights.map((ins, i) => {
              const meta = SEVERITY[ins.severity] || SEVERITY.info;
              return (
                <div key={`${ins.severity}-${ins.title}`} className={`border rounded-lg p-3 text-sm ${meta.cls}`} data-testid={`insight-${i}`}>
                  <div className="flex items-center gap-2 font-medium">
                    <meta.icon className="h-4 w-4 flex-shrink-0" /> {ins.title}
                  </div>
                  <p className="text-xs mt-1 opacity-90">{ins.detail}</p>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <div className="space-y-6">
          {/* Forecast */}
          {forecast && (
            <Card data-testid="finance-forecast">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base">Simple Forecast <Badge variant="outline" className="ml-2 text-[10px]">Estimates</Badge></CardTitle>
                <CardDescription>{forecast.disclaimer}</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground text-xs">Expected monthly income</span><p className="font-bold text-green-600">{formatCurrency(forecast.expected_monthly_income)}</p></div>
                <div><span className="text-muted-foreground text-xs">Expected monthly expenses</span><p className="font-bold text-red-600">{formatCurrency(forecast.expected_monthly_expenses)}</p></div>
                <div><span className="text-muted-foreground text-xs">Expected loan repayments</span><p className="font-bold text-purple-600">{formatCurrency(forecast.expected_loan_repayments)}</p></div>
                <div><span className="text-muted-foreground text-xs">Cash position next month</span><p className={`font-bold ${forecast.expected_cash_position_next_month >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(forecast.expected_cash_position_next_month)}</p></div>
              </CardContent>
            </Card>
          )}

          {/* Reports */}
          <Card data-testid="finance-reports">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2"><FileDown className="h-4 w-4" /> Financial Reports</CardTitle>
              <CardDescription>Filtered by the selections above</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-2">
              <Select value={reportKey} onValueChange={setReportKey}>
                <SelectTrigger className="w-60" data-testid="report-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REPORTS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" disabled={downloading} onClick={() => downloadReport('pdf')} data-testid="report-pdf-btn">PDF</Button>
              <Button variant="outline" size="sm" disabled={downloading} onClick={() => downloadReport('excel')} data-testid="report-excel-btn">Excel</Button>
              <Button variant="outline" size="sm" disabled={downloading} onClick={() => downloadReport('csv')} data-testid="report-csv-btn">CSV</Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Budget vs actual */}
      {data.budget_vs_actual.length > 0 && (
        <Card data-testid="budget-vs-actual">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Budget vs Actual (Projects)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Actual Spend</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.budget_vs_actual.map((b) => (
                  <TableRow key={b.project_id}>
                    <TableCell className="font-medium">{b.project}</TableCell>
                    <TableCell className="text-right">{formatCurrency(b.budget)}</TableCell>
                    <TableCell className="text-right text-red-600">{formatCurrency(b.actual)}</TableCell>
                    <TableCell className={`text-right font-medium ${b.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(b.variance)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Monthly Board Pack */}
      <BoardPackCard />

      {/* School Farm financial analysis */}
      <FarmAnalysisCard />

      {/* AI Assistant */}
      <FinanceAssistant />
    </div>
  );
}
